import { useInView } from 'motion/react'
import { useEffect, useRef, useState, type CSSProperties } from 'react'

const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t))

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Counts up to `value` on first scroll into view, ease-out-expo. Respects
 * prefers-reduced-motion (jumps straight to the value) and renders a real 0 at
 * zero state rather than a placeholder.
 */
export function CountUp({
  value,
  decimals = 0,
  comma = false,
  duration = 1100,
  className,
  style,
}: {
  value: number
  decimals?: number
  comma?: boolean
  duration?: number
  className?: string
  style?: CSSProperties
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-8% 0px' })
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    if (!inView) return
    if (prefersReducedMotion()) {
      // Defer out of the effect body (no synchronous setState-in-effect) — one
      // frame is imperceptible and there is no count-up to run anyway.
      const id = requestAnimationFrame(() => setDisplay(value))
      return () => cancelAnimationFrame(id)
    }
    let raf = 0
    let t0: number | null = null
    const step = (ts: number) => {
      if (t0 === null) t0 = ts
      const p = Math.min((ts - t0) / duration, 1)
      setDisplay(value * easeOutExpo(p))
      raf = p < 1 ? requestAnimationFrame(step) : (setDisplay(value), 0)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [inView, value, duration])

  const fixed = decimals > 0 ? display.toFixed(decimals) : String(Math.round(display))
  const text = comma
    ? Number(fixed).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : fixed

  return (
    <span ref={ref} className={className} style={style}>
      {text}
    </span>
  )
}
